import React, { useCallback, useEffect, useRef, useState } from "react"

import styled from "styled-components"

import { useLocalization } from "@izettle/lingo-client"
import { spacing, typography, IconPlusMedium, IconCircleTickExtraLarge, IconInfoFilledMedium, colors, borderRadius } from "@izettle/otto"
import { Text, Button, Modal, SvgIcon, Loader, LoaderSize} from "@izettle/react"

import { Cancel } from "@/components/cancel"
import { Error } from "@/components/error"
import { ErrorsList } from "@/components/field/field-errors"
import { FileSelector } from "@/components/file-selector"
import { PageWrapper } from "@/components/layout-helpers"
import { useSetPageTitle } from "@/components/page-title"
import { ScreenReaderOnly } from "@/components/screen-reader-only"
import { baseUrl } from "@/constants"
import { focusFieldWithError } from "@/helpers"
import { SelectFilesProps } from "@/models"
import { theme } from "@/theme"

import { InstructionsList } from "../instructions/instructions-list"

import { PreviewItem } from "./preview-item"

import { Requirement } from "."

const UploadText = styled(Text.RegularBold)`
  text-align: center;
`

const PageTitle = styled(Text.Title2)`
  margin: ${spacing.large} ${spacing.none} ${spacing.tiny} ${spacing.none};
`

const Section = styled.section`
  margin-top: ${spacing.medium};
`

const SectionHeading = styled.h2`
  ${typography.smallCaps};
  margin: ${spacing.none} ${spacing.none} ${spacing.xxSmall};
`

const StyledIcon = styled(SvgIcon)`
  @media ${theme.devices.tinyMobile} {
    display: none;
  }

  @media ${theme.devices.mobile} {
    display: inline;
  }
`

const InstructionsButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  padding: ${spacing.small} ${spacing.none};
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media ${theme.devices.mobile} {
    margin-left: ${spacing.small};
  }
`

const InstructionsTitle = styled(Text.Regular)`
  padding: ${spacing.none};
  margin: ${spacing.none};
  text-align: left;
`

const InstructionsText = styled(Text.SmallSecondary)`
  padding: ${spacing.none};
  margin: ${spacing.none};
  text-align: left;
`

const AddFileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
const StyledButtonSvgIcon = styled(SvgIcon)`
  margin-left: ${spacing.xxSmall};
`

const PreviewList = styled.ul`
  list-style-type: none;
  margin: ${spacing.xxSmall} ${spacing.none} ${spacing.none};
  padding: ${spacing.none};
`
const SubmitButton = styled(Button)`
  margin-top: ${spacing.large};
`
const UploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: ${spacing.huge};
`

const HelpText = styled(Text.SmallSecondary)`
  margin: ${spacing.none};
`

const Icon = styled(SvgIcon)`
  margin-top: ${spacing.huge};
  margin-bottom: ${spacing.small};
`

const LoaderWrapper = styled.div`
  margin-top: ${spacing.huge};
  margin-bottom: ${spacing.large};
  padding-top: ${spacing.xxSmall};
`

type UploadProps = {
  files: File[]
  isLoading: boolean
  loadingError: boolean
  isUploading: boolean
  minFiles: number
  maxFiles: number
  fileSuccessfullyAdded: boolean
  successfullySubmittedToBackend: boolean
  addFiles: SelectFilesProps
  removeFile: (name: string) => void
  upload: () => void
  uploadErrors: ErrorsList
  requirements: Requirement[]
}

export const Upload: React.FC<UploadProps> = ({
  files,
  isLoading,
  loadingError,
  isUploading,
  minFiles,
  maxFiles,
  fileSuccessfullyAdded,
  successfullySubmittedToBackend,
  addFiles,
  removeFile,
  upload,
  uploadErrors,
  requirements,
}) => {
  const [errors, setErrors] = useState<ErrorsList>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const instructionsButtonRef = useRef<HTMLButtonElement | null>(null)
  const uploadButtonRef = useRef<HTMLButtonElement | null>(null)
  const { text } = useLocalization()
  const { setPageTitle } = useSetPageTitle()

  useEffect(() => {
    setPageTitle(text("GENERIC_DOC_UPLOAD"))
  }, [])

  useEffect(() => {
    if (uploadErrors.length > 0) focusFieldWithError("genericDocumentUpload")
  }, [uploadErrors])

  useEffect(() => {
    setErrors([])
  }, [files])

  const onClickSubmit = useCallback(() => {
    const tempErrors: ErrorsList = []

    if (files.length < minFiles) {
      tempErrors.push("VALIDATION_REQUIRE_ADDITIONAL_DOCUMENT")
      focusFieldWithError("genericDocumentUpload")
    } else if (files.length > maxFiles) {
      tempErrors.push(text("VALIDATION_EXCEEDED_MAX_AMOUNT_DOCUMENTS", [maxFiles]))
      focusFieldWithError("genericDocumentUpload")
    } else if (files.length === 0 && minFiles === 0) { // This case might happen when the user is requested to contact support and not upload any documents
      tempErrors.push("VALIDATION_REQUIRE_AT_LEAST_ONE_DOCUMENT")
      focusFieldWithError("genericDocumentUpload")
    }

    if (tempErrors.length === 0) upload()
    setErrors(tempErrors)
  }, [files, minFiles, maxFiles, upload])

  const toggleModal = useCallback(() => {
    if (isModalVisible && instructionsButtonRef?.current)
      instructionsButtonRef.current?.focus()
    setIsModalVisible(!isModalVisible)
  }, [isModalVisible, instructionsButtonRef?.current])

  const StyledButton = styled(Button)`
   && {
    border-radius : ${borderRadius.medium} !important;
    border-color: ${colors.border.form} !important;

    &:hover {
    border-color: ${colors.border.focus} !important;
    }

    &:active {
    border-color: ${colors.background.inverted} !important;
    }
   }
  `

  const StyledFileSelector = styled.div`
  && button {
  color : ${colors.text.info} !important;
  }
  `

  if (isLoading) {
    return null
  }

  if (loadingError) {
    return <Error cancelUrl={baseUrl} />
  }

  if (isUploading) {
    return (
      <PageWrapper>
        <UploadWrapper>
          <LoaderWrapper>
            <Loader size={LoaderSize.Large} />
          </LoaderWrapper>
          <UploadText aria-live="polite">{text("UPLOADING_DOCUMENT")}</UploadText>
        </UploadWrapper>
      </PageWrapper>
    )
  }

  if (!isUploading && successfullySubmittedToBackend) {
    return (
      <PageWrapper>
        <ScreenReaderOnly aria-live="polite">
          {successfullySubmittedToBackend ? text("UPLOADING_COMPLETED_LABEL") : ""}
          {successfullySubmittedToBackend ? text("REDIRECTING_TEXT") : ""}
        </ScreenReaderOnly>
        <UploadWrapper>
          <Icon color="success" icon={IconCircleTickExtraLarge} alt="" />
          <UploadText>{text("UPLOADING_COMPLETED_LABEL")}</UploadText>
        </UploadWrapper>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Cancel url={baseUrl} />
      <PageTitle>{text("GENERIC_DOC_UPLOAD")}</PageTitle>
      <Text.Regular>{text("GENERIC_DOC_UPLOAD_SUBTITLE")}</Text.Regular>
      <Section>
        <SectionHeading>{text("GENERIC_DOC_UPLOAD_STEP_1")}</SectionHeading>
        <StyledButton
          ref={instructionsButtonRef}
          size="small"
          fullWidth
          variant="secondary"
          onClick={toggleModal}
        >
          <InstructionsButtonWrapper>
            <StyledIcon icon={IconInfoFilledMedium} color="default" />
            <TextWrapper>
              <InstructionsTitle>
                {text("GENERIC_DOC_UPLOAD_INSTRUCTIONS_TITLE")}
              </InstructionsTitle>
              <InstructionsText>
                {text("GENERIC_DOC_UPLOAD_INSTRUCTIONS_TEXT")}
              </InstructionsText>
            </TextWrapper>
          </InstructionsButtonWrapper>
        </StyledButton>
        <Modal
          id="instructions-modal"
          textLabel="&nbsp;"
          textCloseModal={text("CLOSE_REQUIRED_DOCUMENTS_MODAL")}
          isVisible={isModalVisible}
          onClose={toggleModal}
        >
          <InstructionsList requirements={requirements} />
        </Modal>
      </Section>

      <Section>
        <SectionHeading>{text("GENERIC_DOC_UPLOAD_STEP_2")}</SectionHeading>
        <StyledFileSelector >
        <FileSelector
          ref={uploadButtonRef}
          name="genericDocumentUpload"
          type="generic"
          selectFiles={addFiles}
          redirect={baseUrl}
          multiple
          variant="secondary"
          errors={[...errors, ...uploadErrors]}
          help={
            <HelpText>
              {text("GENERIC_DOC_FILE_COMPLETION_STATUS", [files.length, minFiles])}
            </HelpText>
          }
        >
          <AddFileWrapper>
            <ScreenReaderOnly aria-live="polite">
              {fileSuccessfullyAdded
                ? text("GENERIC_DOC_FILE_COMPLETION_STATUS", [files.length, minFiles])
                : ""}
            </ScreenReaderOnly>
            {text("GENERIC_DOC_UPLOAD_ADD_FILE_BUTTON")}
            <StyledButtonSvgIcon icon={IconPlusMedium} />
          </AddFileWrapper>
        </FileSelector>
        </StyledFileSelector>
        {files.length > 0 && (
          <PreviewList role="list" aria-label={text("GENERIC_DOC_UPLOAD_LIST_TITLE")}>
            {files.map((file) => {
              return <PreviewItem key={file.name} file={file} removeFile={removeFile} />
            })}
          </PreviewList>
        )}
      </Section>

      <SubmitButton onClick={onClickSubmit}>
        {text("GENERIC_DOC_UPLOAD_SUBMIT_BUTTON")}
      </SubmitButton>
    </PageWrapper>
  )
}
